import { useQuery } from '@tanstack/react-query';
import { fetchAirlineByIata, lookupFlight, FlightLookupError, normalizeFlightCode } from '@/services/flight.service';
import { FLIGHT_LOOKUP_FUNCTIONAL_ERRORS, type ResolvedFlightLookup } from '@/types/flight.types';

export const FLIGHT_LOOKUP_QUERY_KEY = 'flightLookup';

/** Il dato di un volo non cambia nell'arco di una compilazione: 15 minuti bastano. */
const FLIGHT_LOOKUP_STALE_TIME = 15 * 60 * 1000;

/**
 * Lookup di un volo dal suo codice, gia' fuso con l'anagrafica `airlines`.
 *
 * `enabled` e' manuale per costruzione: la ricerca parte dal bottone "Cerca", non
 * a ogni tasto digitato — ogni chiamata consuma quota del provider.
 */
export const useFlightLookup = (flightCode: string, enabled: boolean) => {
  const normalized = normalizeFlightCode(flightCode ?? '');

  return useQuery<ResolvedFlightLookup, FlightLookupError>({
    queryKey: [FLIGHT_LOOKUP_QUERY_KEY, normalized],
    queryFn: async () => {
      const flight = await lookupFlight(normalized);
      // L'anagrafica nostra e' un arricchimento, non un requisito: fetchAirlineByIata
      // torna null invece di sollevare, cosi' un volo trovato resta trovato anche se
      // la compagnia non e' in tabella.
      const airline = await fetchAirlineByIata(flight.airline.iata);

      return {
        flight,
        airline: {
          iata: airline?.iata_code || flight.airline.iata,
          // Nome, logo e colore nostri vincono: sono curati, quelli del provider no.
          name: airline?.name || flight.airline.name,
          logoUrl: airline?.logo_url ?? null,
          color: airline?.color ?? null,
          fromDatabase: Boolean(airline),
        },
      };
    },
    enabled: enabled && normalized.length > 0,
    staleTime: FLIGHT_LOOKUP_STALE_TIME,
    gcTime: FLIGHT_LOOKUP_STALE_TIME,
    // Ritentare un codice sbagliato o un volo inesistente non lo fa esistere:
    // si ritenta solo sui guasti (provider/proxy/quota).
    retry: (failureCount, error) => {
      const code = error instanceof FlightLookupError ? error.code : 'unknown';
      if (FLIGHT_LOOKUP_FUNCTIONAL_ERRORS.includes(code)) return false;
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
  });
};
